import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';

const tOptions = { keySeparator: false, nsSeparator: false } as const;

/**
 * Generic hook for translating dynamic/DB-driven labels.
 *
 * Uses the `dynamic-labels` namespace where keys are the original English text.
 * If a translation exists for the current locale, it returns the translated string.
 * Otherwise, falls back to the original text unchanged.
 *
 * Supports composite labels like "Employee count (AU)" by looking up
 * "Employee count" and "AU" separately and reassembling as "translated (translated)".
 *
 * Usage:
 *   const localize = useLocalizedLabel();
 *   <div>{localize('Employee count (Global)')}</div>
 *   <div>{localize('Employee count (AU)')}</div>
 */
export function useLocalizedLabel() {
  const { t, i18n } = useTranslation('dynamic-labels');

  const localize = useCallback(
    (text: string): string => {
      if (!text) return text;

      // Helper: only accept string results from t(), reject objects (nested namespace collisions)
      const safeT = (key: string, fallback: string): string => {
        const result = t(key, { ...tOptions, defaultValue: fallback });
        return typeof result === 'string' ? result : fallback;
      };

      // First try an exact match — use a sentinel defaultValue to detect missing keys
      const MISSING = '__MISSING__';
      const exact = safeT(text, MISSING);
      if (exact !== MISSING) return exact;

      // If no exact match and text has pattern "Something (Something)", try composite lookup
      const match = text.match(/^(.+?)\s*\(([^)]+)\)$/);
      if (match) {
        const prefix = match[1].trim();
        const suffix = match[2].trim();
        const translatedPrefix = safeT(prefix, prefix);
        const translatedSuffix = safeT(suffix, suffix);
        return `${translatedPrefix} (${translatedSuffix})`;
      }

      // Fallback: return original text
      return text;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language],
  );

  return localize;
}
